/** Events (#705): every CE21 event with its people counted by group, and one event's facts (`backend/apps/events/section.py`). The people themselves are the members list's `event` and `eventGroup` filter. */
import { apiRequest } from "../../api/client";
import type { EventGroup } from "../../api/dashboard.types";

export type { EventGroup };

export interface EventRow {
  /** `ce21:<product id>`; the event's page is `/events/<ref>`. */
  ref: string;
  name: string;
  product_type: string | null;
  /** Local days; null when CE21 gives none. */
  starts_on: string | null;
  ends_on: string | null;
  /** Everyone: registrants and vendors, each person once. The three groups add up to it. */
  registered: number;
  members: number;
  non_members: number;
  vendors: number;
  /** Registrations whose CE21 customer Keystone does not mirror: in no list, so in no count. */
  unlinked: number;
  /** Still to come, or happening today. */
  upcoming: boolean;
}

export interface EventsResponse {
  as_of: string;
  count: number;
  /** Upcoming soonest first, then past latest first, then undated. */
  results: EventRow[];
}

export interface EventDetail {
  event: EventRow & { price: string | null; vendor_sources: Array<{ kind: "product" | "group"; name: string }> };
  /** The same event's last edition, or null. */
  previous: { ref: string; name: string; starts_on: string | null; registered: number } | null;
  /** Registered last time, not yet this time (vendors aside); null without a previous edition. */
  came_last_time: number | null;
}

/** GET /api/events/list: every event, upcoming first. */
export function getEvents(): Promise<EventsResponse> {
  return apiRequest<EventsResponse>("/events/list");
}

/** GET /api/events/detail/<ref>. */
export function getEvent(ref: string): Promise<EventDetail> {
  return apiRequest<EventDetail>(`/events/detail/${encodeURIComponent(ref)}`);
}

/** The cache keys: every screen that shows events reads the same entries. */
export const EVENTS_KEY = "events:list";
export const eventKey = (ref: string): string => `events:detail:${ref}`;

/** The event a list opens on: the next one, else the latest that took place. */
export const nextEvent = (events: readonly EventRow[]): EventRow | undefined => events.find((e) => e.upcoming) ?? events[0];

/** An event's page, on one of its groups. The ref's colon stays as it is (a path may hold one), so the address reads as CE21 writes it. */
export function eventHref(ref: string, group: EventGroup = "everyone"): string {
  return `/events/${encodeURIComponent(ref).replace(/%3A/gi, ":")}${group === "everyone" ? "" : `?group=${group}`}`;
}
